import React = require("react");
import {VeBoxLoginCard} from "../components/card/VeBoxLoginCard";
import {Display} from "../components/Display";
import {VeboxLoginInput} from "../components/card/VeboxLoginInput/VeboxLoginInput";
import {Button, useDebounce} from "@vebox/etui-core";
import {Loader} from "../components/Utils/loader/Loader";
import {fetchUsernameValidation} from "./AccountController";
import {isValidPassword, isValidUsername} from "./AccountUtils";
import {ChangeCredentialsPageProps, UsernameValidationResult, ValuesToBeUpdated} from "./Account.types";
import {veboxDefaultTheme} from "../index";

const enum valueType {
    newUsername = "newUsername",
    oldPassword = "oldPassword",
    newPassword = "newPassword",
    repeatPassword = "repeatPassword"
}

export const ChangeCredentialsPage: React.FC<ChangeCredentialsPageProps> = ({
                                                                                texts,
                                                                                changeCredentials,
                                                                                currentUser,
                                                                                changeCredentialsErrorMessage,
                                                                                setIsLoading,
                                                                                isLoading,
                                                                                setChangeCredentialsErrorMessage
                                                                            }) => {

    const [values, setValues] = React.useState<ValuesToBeUpdated>();
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [isSubmittable, setIsSubmittable] = React.useState<boolean>(false);
    const [isDynamicCheck, setIsDynamicCheck] = React.useState<boolean>(false);
    const [validationResponse, setValidationResponse] = React.useState<UsernameValidationResult>();
    const [isValidating, setIsValidating] = React.useState<boolean>(false);
    const [newUsername, setNewUsername] = useDebounce<string>("", 200);


    const updateTextInput = (name: valueType) => (e) => updateValue(name, e.target.value)
    const updateValue = (name: valueType, value: any) => {
        setValues(prevValues => ({
            ...prevValues,
            [name]: value
        }))
        if (name === valueType.newUsername) {
            setNewUsername(value);
        }
    }


    const checkAndChangeCredentials = () => {
        setIsDynamicCheck(true);
        if (isSubmittable && (changeCredentialsErrorMessage ? false : validateCredentials(validationResponse))) {
            setIsLoading(true);
            changeCredentials({
                email: currentUser.email,
                oldPassword: values.oldPassword,
                password: values.newPassword,
                username: values.newUsername
            })
        }
    }

    const usernameValidation = (username: string): boolean => {
        if (!isValidUsername(username)) {
            setErrorMessage(texts.wrongUsernameFormat);
            return false;
        }
        return true;
    }

    const validateCredentials = (validation: UsernameValidationResult): boolean => {
        if (!validation?.usernameValid) {
            setErrorMessage(texts.wrongUsernameFormat);
            return false;
        } else if (!usernameValidation(values.newUsername)) {
            return false;
        } else if (validation?.usernameUsedUp) {
            setErrorMessage(texts.usernameAlreadyTaken);
            return false;
        } else if (isDynamicCheck && (values.newPassword ?? "") !== (values.repeatPassword ?? "")) {
            setErrorMessage(texts.passwordsAreDifferent);
            return false;
        } else if (isDynamicCheck && !isValidPassword(values.newPassword)) {
            setErrorMessage(texts.passwordNotSecure);
            return false;
        } else {
            setErrorMessage("")
            setChangeCredentialsErrorMessage("")
            return true
        }
    }

    const validateSubmittable = (isValid: boolean) => {
        return ((!isDynamicCheck ? (errorMessage === "") : isValid)
            && Boolean(values)
            && (values?.oldPassword !== undefined && values?.oldPassword !== "")
            && (values?.newPassword !== undefined && values?.newPassword !== "")
            && (values?.repeatPassword !== undefined && values?.repeatPassword !== "")
            && (values?.newUsername !== undefined && values?.newUsername !== ""));
    }

    React.useEffect(() => {
        if (newUsername && (isDynamicCheck ? usernameValidation(newUsername) : true)) {
            setIsValidating(true);
            fetchUsernameValidation(values.newUsername, currentUser.email)
                .then((response) => {
                    setValidationResponse(response)
                    validateCredentials(response)
                    setIsValidating(false);
                })
        }
    }, [newUsername])

    React.useEffect(() => {
        if (changeCredentialsErrorMessage) {
            setErrorMessage(changeCredentialsErrorMessage)
        }
    }, [changeCredentialsErrorMessage])

    React.useEffect(() => {
        let valid = true
        if (!isValidating) {
            if (isDynamicCheck) {
                valid = validateCredentials(validationResponse);
            }
            setIsSubmittable(validateSubmittable(valid));
        }
    }, [values, isDynamicCheck, isValidating])

    return (
        <VeBoxLoginCard className={"first-login-card"}>
            <VeBoxLoginCard.Header className={"first-login-card-header"}>
                <VeBoxLoginCard.Title text={texts.changeCredentialsTitle}/>
            </VeBoxLoginCard.Header>
            <VeBoxLoginCard.Body className={"first-login-card-body"}>
                <Display isVisible={!isLoading}>
                    <span className={"first-login-intro-text"}>
                            {texts.firstLoginPageDescription}
                        </span>
                    <Display isVisible={errorMessage}>
                        <p className={"errorInfo"}>{errorMessage}</p>
                    </Display>
                    <VeboxLoginInput
                        disabled={true}
                        type={"text"}
                        value={currentUser?.username}
                    />
                    <VeboxLoginInput
                        type={"text"}
                        placeholder={texts.newUsername}
                        value={values?.newUsername ?? ""}
                        onChange={updateTextInput(valueType.newUsername)}
                    />
                    <VeboxLoginInput
                        type={"password"}
                        value={values?.oldPassword ?? ""}
                        placeholder={texts.oldPassword}
                        onChange={updateTextInput(valueType.oldPassword)}
                    />
                    <VeboxLoginInput
                        type={"password"}
                        value={values?.newPassword ?? ""}
                        placeholder={texts.newPassword}
                        onChange={updateTextInput(valueType.newPassword)}
                    />
                    <VeboxLoginInput
                        type={"password"}
                        value={values?.repeatPassword ?? ""}
                        placeholder={texts.confirmNewPassword}
                        onChange={updateTextInput(valueType.repeatPassword)}
                    />
                </Display>
                <Display isVisible={isLoading}>
                    <div className={"loader-section"}>
                        <Loader color={veboxDefaultTheme.palette.brand.main}/>
                    </div>
                </Display>
            </VeBoxLoginCard.Body>
            <VeBoxLoginCard.Footer className={"first-login-card-footer"}>
                <Display isVisible={!isLoading}>
                    <Button
                        disabled={!isSubmittable}
                        variant={"strong"}
                        onClick={checkAndChangeCredentials}
                    >
                        {texts?.submit}
                    </Button>
                </Display>
            </VeBoxLoginCard.Footer>
        </VeBoxLoginCard>
    )
}
